import { Funnel } from "@phosphor-icons/react";
import clsx from "clsx";
import { SortOrder } from "./../api/common/models";
import { useLocations } from "./../api/location";
import useCollapsibleContent from "./../hooks/useCollapsibleContent";
import Button from "./Button";
import FilterChip from "./FilterChip";
import MultiSelectDropdown, {
    MultiSelectDropdownItemOption,
} from "./MultiSelectFilterToolbarDropdown";
import ResponsiveGroupedItemsCarousel from "./ResponsiveGroupedItemsCarousel";
import SearchBar from "./SearchBar";
import Separator from "./Separator";
import FilterChipGroup from "./FilterChipGroup";
import { toggleItemInArray } from "./../utils/toggleItemInArray";
import { dataStatusOptions, scopeOptions } from "../constants";
import {
    useEmissionsSourceItems,
    usePortfolios,
    useSectors,
} from "../api/emissions-sources";
import { EmissionsSourceOrderBy } from "../api/models/EmissionsSource";

type EmissionsSourcesFilterProps = {
    locationIds: number[];
    portfolioIds: number[];
    sectorIds: number[];
    scopes: number[];
    item: number[];
    type: number[];
    category: number[];
    dataStatus: number[];
    search: string;
    orderBy: EmissionsSourceOrderBy;
    sortOrder: SortOrder;
    additionalActions?: React.ReactNode;
    hideDataStatusFilter?: boolean;
    setLocationIds: (value: number[]) => void;
    setSectorIds: (value: number[]) => void;
    setPortfolioIds: (value: number[]) => void;
    setScopes: (value: number[]) => void;
    setItem: (value: number[]) => void;
    setType: (value: number[]) => void;
    setCategory: (value: number[]) => void;
    setDataStatus: (value: number[]) => void;
    setSearch: (value: string) => void;
};

const EmissionsSourcesFilter = ({
    search,
    setSearch,
    locationIds,
    setLocationIds,
    portfolioIds,
    setPortfolioIds,
    sectorIds,
    setSectorIds,
    scopes,
    setScopes,
    item,
    setItem,
    type,
    setType,
    category,
    setCategory,
    dataStatus,
    setDataStatus,
    additionalActions,
    hideDataStatusFilter,
}: EmissionsSourcesFilterProps) => {
    const { locations } = useLocations();
    const { portfolios } = usePortfolios();
    const { sectors } = useSectors();
    const { emissionsSourceItems } = useEmissionsSourceItems();

    const { expanded, toggle, triggerProps, expandableContentProps } =
        useCollapsibleContent();

    const handleScopeSelect = (value: number) =>
        setScopes(toggleItemInArray(scopes, value));

    const handleDataStatusSelect = (value: number) =>
        setDataStatus(toggleItemInArray(dataStatus, value));

    const portfolioOptions =
        convertNamedIdentifiablesToMultiSelectDropdownOptions(portfolios);

    const sectorOptions =
        convertNamedIdentifiablesToMultiSelectDropdownOptions(sectors);

    const handlePortfolioSelect = (value: number) =>
        setPortfolioIds(toggleItemInArray(portfolioIds, value));
    
    const handleSectorSelect = (value: number) =>
        setSectorIds(toggleItemInArray(sectorIds, value));

    const clearAllFilters = () => {
        setSearch("");
        setLocationIds([]);
        setSectorIds([]);
        setPortfolioIds([]);
        setScopes([]);
        setItem([]);
        setType([]);
        setCategory([]);
        setDataStatus([]);
    };

    const showClearAllButton =
        search !== "" ||
        locationIds.length > 0 ||
        sectorIds.length > 0 ||
        portfolioIds.length > 0 ||
        scopes.length > 0 ||
        item.length > 0 ||
        type.length > 0 ||
        category.length > 0 ||
        dataStatus.length > 0;

    const filterDropdowns = [
        <MultiSelectDropdown
            label="Scope"
            selected={scopes}
            options={scopeOptions}
            onSelect={handleScopeSelect}
            onClear={() => setScopes([])}
            key="filter-dropdown-scope"
        />,
        <MultiSelectDropdown
            label="Portfolio"
            selected={portfolioIds}
            options={portfolioOptions}
            onSelect={handlePortfolioSelect}
            onClear={() => setPortfolioIds([])}
            key="filter-dropdown-portfolio"
        />,
        <MultiSelectDropdown
            label="Sector"
            selected={sectorIds}
            options={sectorOptions}
            onSelect={handleSectorSelect}
            onClear={() => setSectorIds([])}
            key="filter-dropdown-sector"
        />,
    ];

    if (!hideDataStatusFilter) {
        filterDropdowns.push(
            <MultiSelectDropdown
                label="Data Collection"
                selected={dataStatus}
                options={dataStatusOptions}
                onSelect={handleDataStatusSelect}
                onClear={() => setDataStatus([])}
                key="filter-dropdown-data-status"
            />
        );
    }

    return (
        <>
            <div className="flex items-center p-5">
                <Button
                    variant="outline"
                    onClick={toggle}
                    {...triggerProps}
                    className="flex-none"
                >
                    <Funnel width={20} height={20} className="mr-2" />
                    {expanded ? "Hide" : "Show"} filters & sorting
                </Button>
                <div className="flex gap-2 flex-wrap pl-2">
                    {search !== "" && (
                        <FilterChip onRemove={() => setSearch("")}>
                            '{search}'
                        </FilterChip>
                    )}
                    <FilterChipGroup
                        selectedItemIds={locationIds}
                        allItems={locations}
                        labelKey="name"
                        onRemove={setLocationIds}
                    />
                    <FilterChipGroup
                        selectedItemIds={portfolioIds}
                        allItems={portfolios}
                        labelKey="name"
                        onRemove={setPortfolioIds}
                    />
                    <FilterChipGroup
                        selectedItemIds={sectorIds}
                        allItems={sectors}
                        labelKey="name"
                        onRemove={setSectorIds}
                    />
                    <FilterChipGroup
                        selectedItemIds={item}
                        allItems={
                            emissionsSourceItems?.emissionsSourceItems ?? []
                        }
                        labelKey="name"
                        onRemove={setItem}
                    />
                    <FilterChipGroup
                        selectedItemIds={type}
                        allItems={
                            emissionsSourceItems?.emissionsSourceTypes ?? []
                        }
                        labelKey="name"
                        onRemove={setType}
                    />
                    <FilterChipGroup
                        selectedItemIds={category}
                        allItems={
                            emissionsSourceItems?.emissionsSourceCategories ??
                            []
                        }
                        labelKey="name"
                        onRemove={setCategory}
                    />
                    <FilterChipGroup
                        allItems={scopeOptions.map((scope) => ({
                            ...scope,
                            id: scope.value,
                        }))}
                        selectedItemIds={scopes}
                        labelKey="label"
                        onRemove={setScopes}
                    />
                    <FilterChipGroup
                        selectedItemIds={dataStatus}
                        allItems={dataStatusOptions.map((_) => ({
                            ..._,
                            id: _.value,
                        }))}
                        labelKey="label"
                        onRemove={setDataStatus}
                    />
                    {showClearAllButton && (
                        <Button variant="outline" onClick={clearAllFilters}>
                            Clear all
                        </Button>
                    )}
                </div>
                <div className="ml-auto flex-none">{additionalActions}</div>
            </div>
            <Separator />
            <div
                className={clsx("pt-5 px-6", !expanded && "hidden")}
                {...expandableContentProps}
            >
                <SearchBar
                    value={search}
                    onChange={setSearch}
                    placeholder="Search..."
                    additionalSearchElements={
                        <ResponsiveGroupedItemsCarousel
                            items={filterDropdowns}
                            prevTooltipLabel="See previous group of filters"
                            nextTooltipLabel="See next group of filters"
                        />
                    }
                />
            </div>
        </>
    );
};

const convertNamedIdentifiablesToMultiSelectDropdownOptions = (
    namedIdentifiables: { name: string; id: number }[]
): MultiSelectDropdownItemOption<number>[] =>
    namedIdentifiables.map((namedIdentifiable) => ({
        label: namedIdentifiable.name,
        value: namedIdentifiable.id,
    }));

export default EmissionsSourcesFilter;
